<template>
    <div>
        <Panel :title="$t('Search Users')" class="search-container">
            <div class="container-body">
                <cic-textbox :value="searchText" @keyup.enter="filter" @input="(val) => (searchText = val)" />
            </div>
        </Panel>
        <Panel :title="$t('User Licences')" :page="page" :total-rows="filtered.length" @change="changePage">
            <table id="userlicences_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('User') }}</th>
                        <th>{{ $t('Last Assigned Date') }}</th>
                        <th>{{ $t('Closest Expiry Date') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in rows" @click="(evt) => handleOpen(user.userId, evt)">
                        <td>{{ formatUsername(user) }}</td>
                        <td>
                            {{ user.licences && user.licences.length > 0 ? getStart(user) : '-' }}
                        </td>
                        <td>
                            {{ getEnd(user.userId) }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <template v-if="!useDefinitions" #footer>
                <cic-button id="apply_template" @click="openUserTemplate">
                    {{ $t('Apply Template') }}
                </cic-button>
                <cic-button id="unassign_licences" @click="showUnassign = true">
                    {{ $t('Unassign Licences') }}
                </cic-button>
            </template>
        </Panel>

        <EditUserLicences
            v-if="item && !$store.showLoadingOverlay"
            :grace-period="gracePeriod"
            :licences="licences"
            :quantities="quantities"
            :templates="templates"
            :use-definitions="useDefinitions"
            :user="item"
            @blur="item = null"
            @input="saveUserLicences"
        />

        <EditUserTemplate
            v-if="applyTemplate && !$store.showLoadingOverlay"
            :quantities="quantities"
            :licences="licences"
            :templates="templates"
            :users="users"
            @blur="applyTemplate = false"
            @input="(val) => assignLicences(val, loadTable)"
        />

        <UnassignLicences
            v-if="showUnassign && !$store.showLoadingOverlay"
            :grace-period="gracePeriod"
            :quantities="quantities.filter((x) => licences.map((y) => y.quantityId).includes(x.quantityId))"
            :licences="licences"
            :users="users.filter((x) => licences.some((y) => y.assigned.includes(x.userId)))"
            @blur="showUnassign = false"
            @input="(val) => removeLicences(val, loadTable)"
        />

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import Panel from '@/components/container/Panel';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import TableUtils from '@/mixins/TableUtils';
    import { load, save } from '@/utils/Http';
    import { formatEndDate, formatStartDate, formatUsername, loadLicences } from '../LicenceUtils';
    import EditUserLicences from './EditUserLicences';
    import EditUserTemplate from './EditUserTemplate';
    import UnassignLicences from './UnassignLicences';

    export default {
        name: 'UserLicences',
        components: {
            CicButton,
            CicTextbox,
            EditUserLicences,
            EditUserTemplate,
            Panel,
            UnassignLicences,
        },
        mixins: [TableUtils],
        data() {
            return {
                applyTemplate: false,
                editUserLicences: false,
                filtered: [],
                gracePeriod: false,
                item: null,
                licences: [],
                page: 1,
                quantities: [],
                searchText: '',
                showUnassign: false,
                templates: [],
                useDefinitions: false,
                users: [],
            };
        },
        computed: {
            rows() {
                return this.filtered.slice((this.page - 1) * 20, this.page * 20);
            },
        },
        created() {
            this.loadTable();
        },
        methods: {
            formatUsername,
            assignLicences(assignments, callback) {
                save('userlicences/assign', { assignments: JSON.stringify(assignments) }, () => {
                    callback();
                });
            },

            changePage(page) {
                this.page = page;
                this.loadAssigned();
            },

            filter() {
                this.filtered = this.users.filter(
                    (x) => this.searchText === '' || x.username.toLowerCase().includes(this.searchText.toLowerCase()),
                );
                this.changePage(1);
            },

            getEnd(userId) {
                const user = this.licences.filter((x) => x.assigned.includes(userId));
                const ends = user.filter((x) => x.end).map((x) => x.end);
                return user.length > 0 ? formatEndDate(ends.length > 0 ? ends[0] : null) : '-';
            },

            getStart(user) {
                const starts = user.licences.filter((x) => x.assigned).map((x) => x.assigned);
                return formatStartDate(starts.length > 0 ? starts[starts.length - 1] : null);
            },

            loadAssigned() {
                let guids = [];
                const update = this.rows.filter((x) => !x.licences);

                let params = {};
                if (this.useDefinitions) {
                    params = {
                        userIds: update
                            .filter((x) => this.licences.some((y) => y.assigned.includes(x.userId)))
                            .map((x) => x.userId),
                    };
                } else {
                    update.forEach((x) => {
                        guids = guids.concat(
                            this.licences.filter((y) => y.assigned.includes(x.userId)).map((y) => y.id),
                        );
                    });
                    params = { guids: JSON.stringify(guids) };
                }
                if ((this.useDefinitions && params.userIds.length > 0) || (!this.useDefinitions && guids.length > 0)) {
                    load('licencesummary/users', params, (response) => {
                        update.forEach((x) => {
                            x.licences = response.users
                                .filter((y) => y.userId === x.userId)
                                .sort((a, b) => (!a.assigned || a.assigned > b.assigned ? 1 : -1));
                        });
                    });
                }
            },

            loadTable() {
                loadLicences(load, (quantities, licences, gracePeriod, useDefinitions) => {
                    this.quantities = quantities;
                    this.licences = licences;
                    this.gracePeriod = gracePeriod;
                    this.useDefinitions = useDefinitions;
                    this.loadUsers();
                });
            },

            loadUsers() {
                this.item = null;
                this.applyTemplate = false;
                this.editUserLicences = false;
                this.showUnassign = false;
                load('licences/users', {}, (response) => {
                    this.searchText = '';
                    this.users = response.users;
                    this.filter();
                });
            },

            openForm(userId) {
                const user = this.users.find((x) => x.userId === userId);
                this.item = { userId: user.userId, licences: user.licences ? user.licences : [] };
            },

            openUserTemplate() {
                load('licencetemplates/get', { page: -1 }, (response) => {
                    this.templates = response.templates;
                    this.applyTemplate = true;
                });
            },

            removeLicences(removals, callback) {
                save('userlicences/remove', { removals: JSON.stringify(removals) }, () => {
                    callback();
                });
            },

            saveUserLicences(assignments, removals) {
                this.removeLicences([{ userId: this.item.userId, ids: removals }], () => {
                    this.assignLicences([{ userId: this.item.userId, ids: assignments }], this.loadTable);
                });
            },
        },
    };
</script>
